/**
 * impersonationUtils — pure, dependency-free helpers for teacher ⇆ demo
 * account switching. Kept free of imports so it is trivially unit-testable
 * (see tests/unit/impersonationUtils.unit.spec.ts).
 */

/** sessionStorage key holding the snapshotted teacher session while impersonating. */
export const TEACHER_SESSION_BACKUP_KEY = "curio:impersonation:teacher_session:v1";

/**
 * Local-only per-user cache keys (mirrors AuthProvider's TUTOR_CACHE_KEYS).
 * They are keyed by user implicitly (single tab = single user), so they must
 * be wiped on every teacher ⇆ demo switch, otherwise the demo inherits the
 * teacher's `tutor_current_conversation_id` (or vice versa) and trips
 * PGRST116 in `useConversations.checkForSavedConversation`.
 */
export const TUTOR_CACHE_KEYS = [
  "tutor_current_conversation_id",
  "tutor_last_messages",
  "tutor_last_model",
  "tutor_is_temporary_conversation",
] as const;

/** Snapshot of the teacher's Supabase session kept while impersonating. */
export type TeacherSessionBackup = {
  access_token: string;
  refresh_token: string;
  /** The teacher's user id — restore is only ever offered to the same teacher. */
  teacherId: string;
  /** Class whose demo account is being impersonated (for switch-back nav). */
  classId: string;
  demoStudentId: string;
  createdAt: number;
};

/** Must match demoStudentService / create-student-credentials: class UUID prefix. */
export function getDemoWord2ForClass(classId: string): string {
  return classId.substring(0, 8);
}

/** Mirrors AuthPage's words → synthetic email derivation. */
export function sanitizeForEmail(str: string): string {
  return str
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

/** The demo student's login email for a class (used for display/diagnostics only). */
export function getDemoEmailForClass(classId: string): string {
  return `demo-${getDemoWord2ForClass(classId)}@student.example.com`;
}

export function buildTeacherSessionBackup(args: {
  access_token: string;
  refresh_token: string;
  teacherId: string;
  classId: string;
  demoStudentId: string;
}): TeacherSessionBackup {
  return { ...args, createdAt: Date.now() };
}

/** Safe JSON parse + shape validation; returns null for missing/corrupt data. */
export function parseTeacherSessionBackup(raw: string | null): TeacherSessionBackup | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<TeacherSessionBackup>;
    if (
      typeof parsed.access_token === "string" &&
      typeof parsed.refresh_token === "string" &&
      typeof parsed.teacherId === "string" &&
      typeof parsed.classId === "string" &&
      typeof parsed.demoStudentId === "string" &&
      parsed.access_token.length > 0 &&
      parsed.refresh_token.length > 0
    ) {
      return {
        access_token: parsed.access_token,
        refresh_token: parsed.refresh_token,
        teacherId: parsed.teacherId,
        classId: parsed.classId,
        demoStudentId: parsed.demoStudentId,
        createdAt: typeof parsed.createdAt === "number" ? parsed.createdAt : 0,
      };
    }
    return null;
  } catch {
    return null;
  }
}

/** Wipes per-user tutor/conversation cache so it never leaks across the switch. */
export function clearTutorLocalCache(): void {
  try {
    for (const key of TUTOR_CACHE_KEYS) {
      localStorage.removeItem(key);
    }
  } catch (error) {
    console.error("[impersonation] clearTutorLocalCache failed:", error);
  }
}
